import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout, AuthStatus } from '../components/AuthLayout';
import { InlineError } from '../components/Feedback';
import { PasswordField, PasswordStrength } from '../components/Password';
import { account } from '../lib/endpoints';
import { assessPassword, usePasswordPolicy } from '../lib/password';
import { errorMessage } from '../lib/api';
import { useToast } from '../lib/toast';

export function ResetPasswordPage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const policy = usePasswordPolicy();

  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const assessment = assessPassword(password, policy);
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = token !== '' && assessment.satisfied && confirm === password;

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);
    setError(null);
    if (!canSubmit) return;

    setPending(true);
    try {
      await account.resetPassword({ token, newPassword: password });
      toast.success('Password updated', 'Sign in with your new password.');
      navigate('/signin', { replace: true });
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  };

  if (token === '') {
    return (
      <AuthLayout
        title="Choose a new password"
        subtitle="This link is missing its reset token."
        footer={
          <>
            <Link to="/forgot-password">Request a new link</Link>
            <Link to="/signin">Back to sign in</Link>
          </>
        }
      >
        <AuthStatus tone="warn" title="Nothing to reset">
          <p>
            Open the link straight from the email — some mail clients trim long addresses. Reset
            links also expire, so request a fresh one if this keeps happening.
          </p>
        </AuthStatus>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle="Then sign in with it as usual."
      footer={
        <>
          <Link to="/forgot-password">Request a new link</Link>
          <Link to="/signin">Back to sign in</Link>
        </>
      }
      note="Reset links work once and then expire."
    >
      <form className="signin-form" onSubmit={onSubmit} noValidate>
        <PasswordField
          label="New password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          required
          error={
            submitted && !assessment.satisfied ? 'This password does not meet the policy.' : null
          }
        />
        <PasswordField
          label="Confirm new password"
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
          required
          error={
            mismatch || (submitted && confirm !== password)
              ? 'The two passwords do not match.'
              : null
          }
        />

        <PasswordStrength password={password} policy={policy} />

        <InlineError message={error} />

        <button type="submit" className="btn btn-primary btn-block" disabled={pending}>
          {pending ? 'Updating…' : 'Set new password'}
        </button>
      </form>
    </AuthLayout>
  );
}
